// Vendored from ra-data-graphql@5.15.0 (MIT, Copyright (c) 2016-present
// Francois Zaninotto, Marmelab; full license in ./LICENSE-ra-data-graphql.md).
// See ./buildGraphQLProvider.ts for details.
import { type ApolloClient, gql, type TypedDocumentNode } from '@apollo/client';
import {
  getIntrospectionQuery,
  type IntrospectionField,
  type IntrospectionObjectType,
  type IntrospectionQuery,
  type IntrospectionSchema,
  type IntrospectionType,
} from 'graphql';
import {
  CREATE,
  DELETE,
  DELETE_MANY,
  GET_LIST,
  GET_MANY,
  GET_MANY_REFERENCE,
  GET_ONE,
  UPDATE,
  UPDATE_MANY,
} from 'react-admin';

const ALL_TYPES = [
  GET_LIST,
  GET_MANY,
  GET_MANY_REFERENCE,
  GET_ONE,
  CREATE,
  UPDATE,
  DELETE,
  UPDATE_MANY,
  DELETE_MANY,
];

export type IntrospectionOptions = {
  operationNames: Record<string, (type: IntrospectionType) => string>;
};

type IntrospectedResource = {
  type: IntrospectionObjectType;
  [raFetchMethod: string]: IntrospectionField | IntrospectionObjectType;
};

export type IntrospectionResult = {
  types: IntrospectionType[];
  queries: IntrospectionField[];
  resources: IntrospectedResource[];
  schema: IntrospectionSchema;
};

const fetchSchema = async (
  client: ApolloClient,
): Promise<IntrospectionSchema> => {
  const query: TypedDocumentNode<IntrospectionQuery> = gql`
    ${getIntrospectionQuery()}
  `;
  const { data } = await client.query({
    fetchPolicy: 'network-only',
    errorPolicy: 'none',
    query,
  });
  return data.__schema;
};

const getQueriesFromSchema = (
  schema: IntrospectionSchema,
): IntrospectionField[] =>
  schema.types.reduce<IntrospectionField[]>((acc, type) => {
    const { fields } = type as IntrospectionObjectType;
    if (
      type.name !== schema.queryType?.name &&
      type.name !== schema.mutationType?.name &&
      fields
    ) {
      return acc;
    }

    return [...acc, ...(fields ?? [])];
  }, []);

const getTypesFromSchema = (schema: IntrospectionSchema) =>
  schema.types.filter(
    (type) =>
      type.name !== schema.queryType?.name &&
      type.name !== schema.mutationType?.name,
  );

const getOperationNames = (
  type: IntrospectionType,
  { operationNames }: IntrospectionOptions,
) => Object.values(operationNames).map((operation) => operation(type));

const buildResource = (
  type: IntrospectionObjectType,
  queries: IntrospectionField[],
  { operationNames }: IntrospectionOptions,
): IntrospectedResource =>
  ALL_TYPES.reduce<IntrospectedResource>(
    (acc, raFetchMethod) => {
      const operationName = operationNames[raFetchMethod]?.(type);
      const query = queries.find(({ name }) => name === operationName);

      if (!query) {
        return acc;
      }

      return { ...acc, [raFetchMethod]: query };
    },
    { type },
  );

export const introspectSchema = async (
  client: ApolloClient,
  options: IntrospectionOptions,
): Promise<IntrospectionResult> => {
  const schema = await fetchSchema(client);
  const queries = getQueriesFromSchema(schema);
  const types = getTypesFromSchema(schema);
  const resources = types
    .filter((type) =>
      getOperationNames(type, options).some((operation) =>
        queries.some(({ name }) => name === operation),
      ),
    )
    .map((type) =>
      buildResource(type as IntrospectionObjectType, queries, options),
    );

  return { types, queries, resources, schema };
};
